import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { EventsState } from "../../../store/types";
import { BrandliveEvent, LanguagesAbbr, PageModuleType, Session, SessionPreview } from "../../../types/working-model";
import WaitingIndicator from "../../general-ui/waiting-indicator/waiting-indicator";
import MainEventBanner from '../modules/main-event-banner/main-event-banner';
import About from '../modules/about/about';
import Header from "./header/header";
import Speakers from "../modules/speakers/speakers";
import Agenda from '../modules/agenda/agenda';
import { TemplateClassNames } from "../../../types/template-layouts";
import CallOut from "../modules/call-out/call-out";
import Sponsors from "../modules/sponsors/sponsors";
import Products from "../modules/products/products";
import Videos from "../modules/video/videos";
import Footer from "./footer/footer";
import { useHistory, useParams } from "react-router";
import useTranslationModules from "../../../i18n/useTranslationModules";
import BlankSection from "../modules/blank-section/blank-section";
import { eventPath } from "../utils";

interface LandingPageProps {
	previewEventBundle?: BrandliveEvent,
	previewLanguage?: LanguagesAbbr
}

interface LiveModuleMapType {
	[key: number]: any;
}

const LandingPage: React.FC<LandingPageProps> = ({ previewEventBundle, previewLanguage }) => {
	const {
		LiveEventReducer: {
			eventBundle
		}
	} = useSelector((event: EventsState) => event);

	const homepage = eventBundle?.homepage;
	const template = eventBundle?.template.name ? TemplateClassNames[eventBundle?.template.name] : TemplateClassNames.Apollo;
	const sessions = eventBundle?.sessionsPreviews ? eventBundle?.sessionsPreviews as SessionPreview[] : eventBundle?.sessions as Session[];
	const { language, eventName, uuid }: any = useParams();
	const history = useHistory();

	//check to make sure language is in translated list, if not, push them to the default language
	//in this case english is currently locked as the homepage and registration default
	const notValidTranslation = !eventBundle?.homepage?.languages.includes(language);
	const languageToUse = previewLanguage //if in preview module/portal use the passed language beacuse params is not avalable
		? previewLanguage
		: notValidTranslation ? "en" : language;

	useTranslationModules({ language: languageToUse });

	useEffect(() => {
		if (notValidTranslation && (eventName || uuid)) {
			history.replace(`/${eventPath(eventName, uuid)}/en/home`);
		}
	}, [history, eventName, uuid, notValidTranslation]);

	const refMap = useRef<any>({});
	function handleScroll(type: PageModuleType) {
		const fullRef = refMap.current[type];
		if (fullRef) {
			fullRef.scrollIntoView({
				behavior: 'smooth',
				block: 'start'
			});
		}
	}

	const LiveModuleMap: LiveModuleMapType = {
		[PageModuleType.main_event_banner]: MainEventBanner,
		[PageModuleType.speakers]: Speakers,
		[PageModuleType.agenda]: Agenda,
		[PageModuleType.about]: About,
		[PageModuleType.videos]: Videos,
		[PageModuleType.callout]: CallOut,
		[PageModuleType.sponsors]: Sponsors,
		[PageModuleType.products]: Products,
		[PageModuleType.blank]: BlankSection
	};

	const homepageModules = useMemo(() => {
		if (!eventBundle || !homepage) return null;
		return homepage.modules.map(module => {
			if (module.is_on) {
				const Module = LiveModuleMap[module.type];
				// sessions, isLive, isUpNext included for Agenda
				return <Module
					key={module.id}
					module={module}
					template={template}
					ref={(ref: any) => refMap.current[module.type] = ref}
					sessions={sessions}
					isLive={false}
					isUpNext={false}
					eventBundle={eventBundle}
				/>;
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventBundle, homepage, template, sessions]);

	const languages = Array.from(new Set(homepage?.languages as LanguagesAbbr[])); // new Set to Array for deduping

	// Preview Event variables

	const previewHomepage = previewEventBundle?.homepage;
	const previewTemplate = previewEventBundle?.template.name ? TemplateClassNames[previewEventBundle?.template.name] : TemplateClassNames.Apollo;
	const previewSessions = previewEventBundle?.sessionsPreviews ? previewEventBundle?.sessionsPreviews as SessionPreview[] : previewEventBundle?.sessions as Session[];

	const previewHomepageModules = useMemo(() => {
		if (!previewEventBundle || !previewHomepage) return null;

		return previewHomepage.modules.map(module => {
			if (module.is_on) {
				const Module = LiveModuleMap[module.type];
				return <Module
					key={module.id}
					module={module}
					template={previewTemplate}
					ref={(ref: any) => refMap.current[module.type] = ref}
					sessions={previewSessions}
					isLive={false}
					isUpNext={false}
					eventBundle={previewEventBundle}
				/>;
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [previewEventBundle, previewHomepage, previewTemplate, previewSessions]);

	const previewLanguages = Array.from(new Set(previewHomepage?.languages as LanguagesAbbr[]));

	if (previewEventBundle) {
		if (!previewHomepage) return <></>;
		return (
			<div>
				<Header isUserLoggedIn={false} header={previewHomepage.header} languages={previewLanguages} template={previewTemplate} registration_on={!!previewEventBundle?.registration_on} handleScroll={handleScroll} previewLanguage={previewLanguage} />
				{previewHomepageModules}
				{previewHomepage.footer && <Footer footer={previewHomepage.footer} template={previewTemplate} previewLanguage={previewLanguage} />}
			</div>
		);
	} else {
		if (!eventBundle) return <WaitingIndicator />;
		if (!homepage) return <h1>TODO: No Home page(reroute?)</h1>;

		return (
			<div>
				<Header isUserLoggedIn={false} header={homepage.header} languages={languages} template={template} registration_on={!!eventBundle?.registration_on} handleScroll={handleScroll} />
				{homepageModules}
				{homepage.footer && <Footer footer={homepage.footer} template={template} />}
			</div>
		);
	}
};

export default LandingPage;